<template>
  <div class="code-digits" role="group" :aria-label="label">
    <input
      v-for="(_, index) in 6"
      :key="index"
      ref="inputs"
      type="text"
      inputmode="numeric"
      maxlength="1"
      :value="modelValue[index]"
      :aria-label="`${label} ${index + 1}`"
      :disabled="disabled"
      autocomplete="one-time-code"
      class="code-digit ym-disable-keys"
      @input="handleInput($event, index)"
      @keydown.delete.prevent="handleDelete(index)"
      @paste.prevent="handlePaste"
    />
  </div>
</template>

<script setup lang="ts">
// Extracted from the VPN registration OTP step; both screens use the same fields.
import { nextTick, ref } from "vue";

const props = defineProps<{ modelValue: string[]; label: string; disabled?: boolean }>();
const emit = defineEmits<{ "update:modelValue": [digits: string[]]; complete: [] }>();
const inputs = ref<HTMLInputElement[]>([]);

function focus(index = 0): void {
  if (!props.disabled) inputs.value[index]?.focus();
}

function handleInput(event: Event, index: number): void {
  if (props.disabled) return;
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const digits = [...props.modelValue];
  digits[index] = /^\d*$/.test(value) ? value.slice(-1) : "";
  input.value = digits[index];
  emit("update:modelValue", digits);
  if (digits[index] && index < 5) focus(index + 1);
}

function handleDelete(index: number): void {
  if (props.disabled) return;
  const digits = [...props.modelValue];
  if (digits[index]) digits[index] = "";
  else if (index > 0) {
    digits[index - 1] = "";
    focus(index - 1);
  }
  emit("update:modelValue", digits);
}

function handlePaste(event: ClipboardEvent): void {
  if (props.disabled) return;
  const numbers = event.clipboardData?.getData("text").match(/\d/g);
  if (!numbers) return;
  emit(
    "update:modelValue",
    Array.from({ length: 6 }, (_, index) => numbers[index] ?? ""),
  );
  focus(Math.min(numbers.length, 5));
  if (numbers.length >= 6) void nextTick(() => emit("complete"));
}

defineExpose({ focus });
</script>

<style scoped lang="scss">
.code-digits {
  display: flex;
  gap: 8px;
  justify-content: center;
}
.code-digit {
  width: 48px;
  min-width: 0;
  height: 56px;
  border: 1px solid var(--ds-border);
  border-radius: var(--ds-radius-card);
  text-align: center;
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-semibold);
  color: var(--ds-text);
  background: var(--ds-surface-canvas);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: var(--ds-accent);
    background: var(--ds-surface);
    box-shadow: 0 0 0 4px var(--ds-accent-soft-15);
  }
}
</style>
